import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Calendar, 
  Newspaper, 
  Bell, 
  TrendingUp, 
  DollarSign,
  Scissors,
  ExternalLink,
  BarChart3,
} from "lucide-react";
import type { AssetFundamentals as FundamentalsType } from "@/schemas";

interface EventsNewsTabProps {
  fundamentals: FundamentalsType | null;
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export function EventsNewsTab({ fundamentals }: EventsNewsTabProps) {
  const news = fundamentals?.news || [];
  const earningsEvents = fundamentals?.earnings_calendar?.earnings_dates || [];
  
  // Sort earnings by date descending (most recent first)
  const sortedEarnings = [...earningsEvents].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Find next upcoming earnings (future date)
  const now = new Date();
  const nextEarnings = sortedEarnings.find(e => new Date(e.date) > now);
  const pastEarnings = sortedEarnings.filter(e => new Date(e.date) <= now);

  return (
    <div className="space-y-6">
      {/* Upcoming Events Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Events
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Next Earnings */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Next Earnings</span>
              </div>
              {nextEarnings ? (
                <div>
                  <span className="text-lg font-semibold">
                    {formatDate(nextEarnings.date)}
                  </span>
                  {nextEarnings.eps_estimate && (
                    <p className="text-xs text-muted-foreground mt-1">
                      EPS Est: ${nextEarnings.eps_estimate.toFixed(2)}
                    </p>
                  )}
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Not scheduled</span>
              )}
            </div>

            {/* Next Dividend */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium">Recent Dividend</span>
              </div>
              {fundamentals?.recent_dividends?.[0] ? (
                <div>
                  <span className="text-lg font-semibold">
                    ${fundamentals.recent_dividends[0].amount.toFixed(4)}
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(fundamentals.recent_dividends[0].date)}
                  </p>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">No dividends</span>
              )}
            </div>

            {/* Recent Split */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-2">
                <Scissors className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">Recent Split</span>
              </div>
              {fundamentals?.splits && fundamentals.splits.length > 0 ? (
                <div>
                  <span className="text-lg font-semibold">
                    {fundamentals.splits[0].ratio}:1
                  </span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(fundamentals.splits[0].date)}
                  </p>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">No recent splits</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Earnings History */}
      {pastEarnings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Earnings History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">EPS Estimate</TableHead>
                  <TableHead className="text-right">EPS Actual</TableHead>
                  <TableHead className="text-right">Surprise</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pastEarnings.slice(0, 8).map((earning, idx) => {
                  const surprise = earning.eps_actual && earning.eps_estimate
                    ? ((earning.eps_actual - earning.eps_estimate) / Math.abs(earning.eps_estimate))
                    : null;
                  
                  return (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">
                        {formatDate(earning.date)}
                      </TableCell>
                      <TableCell className="text-right">
                        {earning.eps_estimate ? `$${earning.eps_estimate.toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {earning.eps_actual ? `$${earning.eps_actual.toFixed(2)}` : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        {surprise !== null ? (
                          <div className="flex flex-col items-end gap-1.5">
                            <span className={`text-xs font-semibold ${surprise >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                              {surprise >= 0 ? "+" : ""}{(surprise * 100).toFixed(1)}%
                            </span>
                            <div className="relative w-24 h-1.5 bg-muted rounded-full overflow-hidden flex items-center">
                              {/* Center line */}
                              <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-background z-10" />
                              {surprise >= 0 ? (
                                <div 
                                  className="absolute left-1/2 h-full bg-emerald-500 rounded-r-full" 
                                  style={{ width: `${Math.min(surprise * 200, 50)}%` }} 
                                />
                              ) : (
                                <div 
                                  className="absolute right-1/2 h-full bg-red-500 rounded-l-full" 
                                  style={{ width: `${Math.min(Math.abs(surprise) * 200, 50)}%` }} 
                                />
                              )}
                            </div>
                          </div>
                   
                        ) : "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Analyst Recommendations */}
      {fundamentals?.recent_recommendations && fundamentals.recent_recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Analyst Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {fundamentals.recent_recommendations.slice(0, 8).map((rec, idx) => (
                <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-sm">{rec.firm}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(rec.date)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {rec.from_grade && (
                      <>
                        <Badge variant="outline" className="text-xs">{rec.from_grade}</Badge>
                        <span className="text-muted-foreground">→</span>
                      </>
                    )}
                    <Badge 
                      variant="outline" 
                      className={`text-xs ${
                        rec.to_grade?.toLowerCase().includes("buy") || rec.to_grade?.toLowerCase().includes("outperform") 
                          ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" :
                        rec.to_grade?.toLowerCase().includes("sell") || rec.to_grade?.toLowerCase().includes("underperform")
                          ? "bg-red-500/10 text-red-500 border-red-500/20" :
                          ""
                      }`}
                    >
                      {rec.to_grade}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* News Headlines */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            Recent News
          </CardTitle>
        </CardHeader>
        <CardContent>
          {news.length > 0 ? (
            <div className="space-y-4">
              {news.map((article, idx) => (
                <a 
                  key={article.uuid || idx}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <h4 className="font-medium text-sm line-clamp-2">{article.title}</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        {article.publisher} • {formatDate(article.publish_time)}
                      </p>
                    </div>
                    <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-muted rounded-lg bg-muted/10">
              <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                <Newspaper className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-medium">No recent news</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm text-center">
                We couldn&apos;t find any recent news articles for this asset at this time.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
